'use client';

import { c as _c } from "react/compiler-runtime";
import { type Factiii, type PostFactiii } from '@shared/all/domains/factiiis';
import { isUrl } from '@shared/all/validators';
import formatRelativeTime from '@shared/utils/helpers/formatRelativeTime';
import { tagValues } from '@shared/utils/helpers/tagValues';
import { BookOpen, Clock, Database, Hash, Link as LinkIcon, MapPin } from 'lucide-react';
import Avatar from '../common/Avatar';
import AutoTagNotice from './AutoTagNotice';
type Props = {
  factiii: Factiii;
  pfc: PostFactiii;
  // The post the card is shown on. With it, a mood or topic the Auto Tag tool
  // placed is labelled and its author can remove it; omitted in read-only
  // contexts.
  post?: {
    id: string;
    isAuthor: boolean;
  };
  // When an entry has raw scrape content, the card shows a "Raw Data" button
  // that opens it. Only wired up where the card is interactive (the popover);
  // omitted in read-only contexts.
  onViewRawData?: (scrapeId: number) => void;
};

// Presentational summary of a factiii on a post: avatar, name, entry/time
// meta, description, attribute counts, and who tagged it. Shared by the
// avatar hover card and the links panel so both render identically. Callers
// own the surrounding container (padding/background/positioning).
export default function FactiiiSummaryCard(t0) {
  const $ = _c(69);
  const {
    factiii,
    pfc,
    post,
    onViewRawData
  } = t0;
  const entries = pfc.postFactiiis;
  const entryCount = entries.length;
  let t1;
  if ($[0] !== entries) {
    t1 = entries.find(_temp);
    $[0] = entries;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rawDataEntry = t1;
  let t2;
  if ($[2] !== entries) {
    t2 = Array.from(new Map(entries.filter(_temp2).map(_temp3)).values());
    $[2] = entries;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const taggers = t2;
  let t3;
  if ($[4] !== taggers) {
    t3 = taggers.slice(0, 5);
    $[4] = taggers;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const visibleTaggers = t3;
  const extraTaggers = Math.max(0, taggers.length - visibleTaggers.length);
  let latestTime;
  let linkCount;
  let pinCount;
  let refCount;
  let t4;
  let t5;
  let t6;
  if ($[6] !== entries || $[7] !== entryCount || $[8] !== factiii.avatarKey || $[9] !== factiii.description || $[10] !== factiii.id || $[11] !== factiii.name || $[12] !== factiii.status) {
    latestTime = entries.reduce(_temp4, null);
    pinCount = entries.reduce(_temp6, 0);
    let t7;
    if ($[20] !== entries) {
      t7 = entries.filter(_temp7);
      $[20] = entries;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    linkCount = t7.length;
    const values = tagValues(factiii.id, entries);
    refCount = entries.reduce(_temp8, 0);
    const t8 = factiii.status === "RETIRED";
    let t9;
    if ($[22] !== factiii.avatarKey || $[23] !== factiii.id || $[24] !== t8) {
      t9 = <Avatar factiiiAvatarKey={factiii.avatarKey} factiiiId={factiii.id} size={36} isRetired={t8} />;
      $[22] = factiii.avatarKey;
      $[23] = factiii.id;
      $[24] = t8;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    let t10;
    if ($[26] !== factiii.name) {
      t10 = <span className="truncate">{factiii.name}</span>;
      $[26] = factiii.name;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t11;
    if ($[28] !== factiii.status) {
      t11 = factiii.status === "RETIRED" && <span className="shrink-0 text-xs font-normal text-textGray">(Retired)</span>;
      $[28] = factiii.status;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    let t12;
    if ($[30] !== t10 || $[31] !== t11) {
      t12 = <div className="flex items-center gap-1 truncate font-semibold text-textPrimary">{t10}{t11}</div>;
      $[30] = t10;
      $[31] = t11;
      $[32] = t12;
    } else {
      t12 = $[32];
    }
    const t13 = entryCount === 1 ? "y" : "ies";
    const t14 = latestTime && <>{" \xB7 "}{formatRelativeTime(latestTime)}</>;
    let t15;
    if ($[33] !== entryCount || $[34] !== t13 || $[35] !== t14) {
      t15 = <div className="text-xs text-textSecondary">{entryCount} entr{t13}{t14}</div>;
      $[33] = entryCount;
      $[34] = t13;
      $[35] = t14;
      $[36] = t15;
    } else {
      t15 = $[36];
    }
    let t16;
    if ($[37] !== t12 || $[38] !== t15) {
      t16 = <div className="min-w-0 flex-1">{t12}{t15}</div>;
      $[37] = t12;
      $[38] = t15;
      $[39] = t16;
    } else {
      t16 = $[39];
    }
    if ($[40] !== t16 || $[41] !== t9) {
      t4 = <div className="flex items-center gap-2">{t9}{t16}</div>;
      $[40] = t16;
      $[41] = t9;
      $[42] = t4;
    } else {
      t4 = $[42];
    }
    if ($[43] !== factiii.description) {
      t5 = factiii.description && <p className="mt-2 line-clamp-2 text-xs text-textSecondary">{factiii.description}</p>;
      $[43] = factiii.description;
      $[44] = t5;
    } else {
      t5 = $[44];
    }
    t6 = values.length > 0 && <ul className="mt-2 flex flex-col gap-0.5">{values.map(_temp9)}</ul>;
    $[6] = entries;
    $[7] = entryCount;
    $[8] = factiii.avatarKey;
    $[9] = factiii.description;
    $[10] = factiii.id;
    $[11] = factiii.name;
    $[12] = factiii.status;
    $[13] = latestTime;
    $[14] = linkCount;
    $[15] = pinCount;
    $[16] = refCount;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    latestTime = $[13];
    linkCount = $[14];
    pinCount = $[15];
    refCount = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
  }
  let t7;
  if ($[45] !== latestTime || $[46] !== linkCount || $[47] !== pinCount || $[48] !== refCount) {
    t7 = (pinCount > 0 || linkCount > 0 || refCount > 0) && <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-textSecondary">{pinCount > 0 && <span className="flex items-center gap-1"><MapPin size={12} />{pinCount}</span>}{linkCount > 0 && <span className="flex items-center gap-1"><LinkIcon size={12} />{linkCount}</span>}{refCount > 0 && <span className="flex items-center gap-1"><BookOpen size={12} />{refCount}</span>}{latestTime && pinCount + linkCount + refCount === 0 && <span className="flex items-center gap-1"><Clock size={12} />{formatRelativeTime(latestTime)}</span>}</div>;
    $[45] = latestTime;
    $[46] = linkCount;
    $[47] = pinCount;
    $[48] = refCount;
    $[49] = t7;
  } else {
    t7 = $[49];
  }
  let t8;
  if ($[50] !== onViewRawData || $[51] !== rawDataEntry) {
    t8 = rawDataEntry?.scrapeId != null && onViewRawData && <button type="button" onClick={e => {
      e.stopPropagation();
      onViewRawData(rawDataEntry.scrapeId as number);
    }} className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md border border-ds-border-standard bg-background-primary px-2.5 py-1.5 text-xs font-emphasis text-textSecondary transition-colors hover:border-primary/40 hover:text-textPrimary"><Database size={14} />Raw Data</button>;
    $[50] = onViewRawData;
    $[51] = rawDataEntry;
    $[52] = t8;
  } else {
    t8 = $[52];
  }
  let t9;
  if ($[53] !== extraTaggers || $[54] !== taggers.length || $[55] !== visibleTaggers) {
    t9 = taggers.length > 0 && <div className="mt-3 border-t border-ds-border-subtle pt-2"><div className="mb-1 text-[11px] uppercase tracking-wide text-textGray">Tagged by</div><div className="flex items-center gap-1.5"><div className="flex -space-x-1.5">{visibleTaggers.map(_temp10)}</div><span className="min-w-0 truncate text-xs text-textSecondary">{visibleTaggers.map(_temp11).join(", ")}{extraTaggers > 0 && ` +${extraTaggers}`}</span></div></div>;
    $[53] = extraTaggers;
    $[54] = taggers.length;
    $[55] = visibleTaggers;
    $[56] = t9;
  } else {
    t9 = $[56];
  }
  let t10;
  if ($[57] !== entries || $[58] !== factiii || $[59] !== post) {
    t10 = post && <AutoTagNotice factiii={factiii} tags={entries} postId={post.id} isAuthor={post.isAuthor} />;
    $[57] = entries;
    $[58] = factiii;
    $[59] = post;
    $[60] = t10;
  } else {
    t10 = $[60];
  }
  let t11;
  if ($[61] !== t10 || $[62] !== t4 || $[63] !== t5 || $[64] !== t6 || $[65] !== t7 || $[66] !== t8 || $[67] !== t9) {
    t11 = <>{t4}{t5}{t6}{t7}{t8}{t9}{t10}</>;
    $[61] = t10;
    $[62] = t4;
    $[63] = t5;
    $[64] = t6;
    $[65] = t7;
    $[66] = t8;
    $[67] = t9;
    $[68] = t11;
  } else {
    t11 = $[68];
  }
  return t11;
}
function _temp11(u_0) {
  return u_0.name || `@${u_0.username}`;
}
function _temp10(u) {
  return <Avatar key={u.id} avatarKey={u.avatarKey} robohash={u.robohash} isBot={u.tag === "BOT"} size={20} userId={u.id} border={true} />;
}
function _temp9(v, i) {
  return <li key={`${v.factiiiId}-${i}`} className="flex items-center gap-1.5 text-sm font-semibold text-textPrimary"><Hash size={12} className="shrink-0 text-textSecondary" /><span className="min-w-0 break-words">{v.label}</span></li>;
}
function _temp8(n_0, pf_5) {
  return n_0 + (pf_5.references?.length ?? 0) + (pf_5.factiiiReferences?.length ?? 0);
}
function _temp7(pf_4) {
  return pf_4.data && isUrl(pf_4.data);
}
function _temp6(n, pf_3) {
  return n + pf_3.spaceTimes.filter(_temp5).length;
}
function _temp5(st) {
  return st.latitude != null && st.longitude != null;
}
function _temp4(acc, pf_2) {
  if (!pf_2.createdAt) {
    return acc;
  }
  const ts = new Date(pf_2.createdAt);
  return !acc || ts.getTime() > acc.getTime() ? ts : acc;
}
function _temp3(pf_1) {
  return [pf_1.user.id, pf_1.user];
}
function _temp2(pf_0) {
  return !!pf_0.user;
}
function _temp(pf) {
  return pf.rawData && pf.scrapeId != null;
}